import { UiSchema } from '@rjsf/utils';
import { isArray } from 'lodash/fp';
import { useCallback, useMemo, VFC } from 'react';

import { ChangeValue } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { ValueDiffLabel } from 'dashboard/apps/InquiryTemplateEditor/ValueDiffLabel';
import { PropertyRow } from 'dashboard/apps/InquiryTemplateEditor/VersionDiffTable';
import { WorkflowActionMeta } from 'dashboard/models/workflow';
import { AllowedActionMetaType } from 'dashboard/models/workflow-schema';
import { WorkflowActionFromConfig } from 'dashboard/models/workflow-version';

interface WorkflowActionDiffActionMetaProps {
  actionParameters: WorkflowActionFromConfig['params'];
  actionMeta: WorkflowActionMeta;
  parameterDiff: ChangeValue | null;
  schema: UiSchema;
  schemaKey: string;
}

const WorkflowActionDiffActionMeta: VFC<WorkflowActionDiffActionMetaProps> = ({
  actionParameters,
  parameterDiff,
  schema,
  schemaKey,
  actionMeta,
}) => {
  const actionMetaObjects = useMemo(
    () =>
      (schema['ui:options']?.enumTypeOptions as AllowedActionMetaType[]).flatMap(
        (actionMetaType) => actionMeta[actionMetaType],
      ),
    [schema, actionMeta],
  );

  const getMetaLabel = useCallback(
    (parameterKey: string) => {
      let parameterName = null;
      // Graph Query Templates do not inheirt from ActionMetaBase
      // and need to be destructured differently
      if (
        (schema?.['ui:options']?.enumTypeOptions as (keyof WorkflowActionMeta)[])?.includes(
          'graph-query-templates',
        )
      ) {
        parameterName = actionMeta['graph-query-templates'].find(
          (template) => template.id === parameterKey,
        )?.attributes?.name;
      } else {
        parameterName = actionMetaObjects.find((object) => object.token === parameterKey)?.name;
      }

      return parameterName ?? schema['ui:placeholder'] ?? 'None';
    },
    [actionMetaObjects, schema, actionMeta],
  );

  const getActionParameterName = useCallback(
    (
      parameterKey: WorkflowActionFromConfig['params'][keyof WorkflowActionFromConfig['params']],
    ) => {
      {
        if (parameterKey != null && isArray(parameterKey)) {
          return parameterKey.map((key) => getMetaLabel(key)).join(', ');
        } else {
          return getMetaLabel(parameterKey as string);
        }
      }
    },
    [getMetaLabel],
  );

  return (
    <>
      <PropertyRow
        textKey={schema['ui:title']!}
        value={
          parameterDiff ? (
            <ValueDiffLabel
              valueDiff={{
                added: getActionParameterName(parameterDiff.value),
                removed: getActionParameterName(parameterDiff['prev-value']),
              }}
              fallbackValue='None'
              vertical
            />
          ) : (
            getActionParameterName(actionParameters[schemaKey])
          )
        }
      />
    </>
  );
};

export default WorkflowActionDiffActionMeta;
